/**
 * Routes of the customer app. Final URLs per HANDOFF §0.11a (ids are UUIDs, the stage is a path segment); later
 * milestones fill the pages.
 *
 * Permission-aware routing: a route's `staticData.permission` (11 §3) is checked by the shell against `GET /me`
 * (`RouteGate`): without it the page says "You can't open …" (screen 37 `no-permission`); the sidebar hides the
 * same items. The server enforces either way.
 *
 * REQUESTS #6: TanStack Router decodes the path with decodeURI (which turns %2E into ".") before matching and
 * decodeURIComponent()s params. So an unsafe raw path never reaches matching: `rewrite.input` replaces it with
 * a path no route has, which renders "We can't find this page". Id params are UUIDs or "not found".
 */
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  notFound,
  Outlet,
  useRouter,
  type RouterHistory,
} from "@tanstack/react-router";
import type { TFunction } from "i18next";
import {
  interceptLinks,
  isSafeAppPath,
  isStageId,
  isUuid,
  NotFoundState,
  ServerErrorState,
  type PermissionRule,
} from "@brevalt/app-core";
import { isApiError } from "@brevalt/api-client";
import { CASE_COVERAGE_STATES } from "./shared/case-coverage";
import { preloadRouteNamespace } from "@brevalt/app-core/locale-runtime";
/**
 * A route group's page and its copy in the active locale load together (L1): the page's hook (`useCasesCopy`)
 * would otherwise start the copy's chunk only after the page's chunk arrived.
 */
function withCopy<T>(namespace: string, page: () => Promise<T>): () => Promise<T> {
  return () => {
    preloadRouteNamespace(namespace);
    return page();
  };
}

// Pages load on demand (L8): the initial bundle carries the shell, not the pages.
const HomePage = lazyRouteComponent(
  withCopy("app.dashboard", () => import("./dashboard/home")),
  "HomePage",
);
const HelpPage = lazyRouteComponent(
  withCopy("app.assistant", () => import("./assistant/help")),
  "HelpPage",
);
const SupportThreadRoute = lazyRouteComponent(
  withCopy("app.assistant", () => import("./assistant/help")),
  "SupportThreadRoute",
);
const CasesPage = lazyRouteComponent(
  withCopy("app.cases", () => import("./cases/list")),
  "CasesPage",
);
const CaseRedirect = lazyRouteComponent(
  withCopy("app.cases", () => import("./cases/case")),
  "CaseRedirect",
);
const CaseStagePage = lazyRouteComponent(
  withCopy("app.cases", () => import("./cases/case")),
  "CaseStagePage",
);
const NoticePage = lazyRouteComponent(
  withCopy("app.cases", () => import("./cases/notice")),
  "NoticePage",
);
const NotificationsPage = lazyRouteComponent(() => import("./routes/notifications"), "NotificationsPage");
const MorePage = lazyRouteComponent(() => import("./routes/more"), "MorePage");
const ProductsPage = lazyRouteComponent(
  withCopy("app.products", () => import("./products/list")),
  "ProductsPage",
);
const ProductPage = lazyRouteComponent(
  withCopy("app.products", () => import("./products/product")),
  "ProductPage",
);
const ProductSourcesPage = lazyRouteComponent(
  withCopy("app.products", () => import("./products/product")),
  "ProductSourcesPage",
);
const VersionPage = lazyRouteComponent(
  withCopy("app.products", () => import("./products/version")),
  "VersionPage",
);
const DiffPage = lazyRouteComponent(
  withCopy("app.products", () => import("./products/diff")),
  "DiffPage",
);
const AcceptInvitationPage = lazyRouteComponent(() => import("./invitations/accept"), "AcceptInvitationPage");
const FindingsPage = lazyRouteComponent(
  withCopy("app.findings", () => import("./findings/list")),
  "FindingsPage",
);
const FindingPage = lazyRouteComponent(
  withCopy("app.findings", () => import("./findings/detail")),
  "FindingPage",
);
const OnboardingFrame = lazyRouteComponent(
  withCopy("app.onboarding", () => import("./onboarding/frame")),
  "OnboardingFrame",
);
const OnboardingStart = lazyRouteComponent(
  withCopy("app.onboarding", () => import("./onboarding/start")),
  "OnboardingStart",
);
const CompanyPage = lazyRouteComponent(
  withCopy("app.onboarding", () => import("./onboarding/company")),
  "CompanyPage",
);
const FirstProductPage = lazyRouteComponent(
  withCopy("app.onboarding", () => import("./onboarding/product")),
  "ProductPage",
);
const SourcesPage = lazyRouteComponent(
  withCopy("app.onboarding", () => import("./onboarding/sources")),
  "SourcesPage",
);
const ScanPage = lazyRouteComponent(
  withCopy("app.onboarding", () => import("./onboarding/scan")),
  "ScanPage",
);
const ResultsPage = lazyRouteComponent(
  withCopy("app.onboarding", () => import("./onboarding/results")),
  "ResultsPage",
);
const GitHubCallback = lazyRouteComponent(
  withCopy("app.onboarding", () => import("./onboarding/integration-callback")),
  "GitHubCallback",
);
const GitLabCallback = lazyRouteComponent(
  withCopy("app.onboarding", () => import("./onboarding/integration-callback")),
  "GitLabCallback",
);
const IntegrationsPage = lazyRouteComponent(
  withCopy("app.workspace", () => import("./workspace/integrations")),
  "IntegrationsPage",
);
const WebhookPage = lazyRouteComponent(
  withCopy("app.workspace", () => import("./workspace/webhooks")),
  "WebhookPage",
);
const TeamPage = lazyRouteComponent(
  withCopy("app.workspace", () => import("./workspace/team")),
  "TeamPage",
);
const RolesPage = lazyRouteComponent(
  withCopy("app.workspace", () => import("./workspace/team")),
  "RolesPage",
);
const SettingsLayout = lazyRouteComponent(
  withCopy("app.workspace", () => import("./workspace/settings")),
  "SettingsLayout",
);
const SettingsIndex = lazyRouteComponent(
  withCopy("app.workspace", () => import("./workspace/settings")),
  "SettingsIndex",
);
const MeSettingsPage = lazyRouteComponent(
  withCopy("app.workspace", () => import("./workspace/settings")),
  "MeSettingsPage",
);
const CompanySettingsPage = lazyRouteComponent(
  withCopy("app.onboarding", () => import("./onboarding/company")),
  "CompanySettingsPage",
);
const SecuritySettingsPage = lazyRouteComponent(
  withCopy("app.workspace", () => import("./workspace/security")),
  "SecuritySettingsPage",
);
const NotificationSettings = lazyRouteComponent(
  withCopy("app.workspace", () => import("./workspace/notifications")),
  "NotificationSettingsPage",
);
const DataSettingsPage = lazyRouteComponent(
  withCopy("app.workspace", () => import("./workspace/data")),
  "DataSettingsPage",
);
const PreferencesPage = lazyRouteComponent(
  withCopy("app.workspace", () => import("./workspace/preferences")),
  "PreferencesPage",
);
const AuditPage = lazyRouteComponent(
  withCopy("app.workspace", () => import("./workspace/audit")),
  "AuditPage",
);
const BillingPage = lazyRouteComponent(
  withCopy("app.workspace", () => import("./workspace/billing")),
  "BillingPage",
);
const DocumentsIndex = lazyRouteComponent(
  withCopy("app.documents", () => import("./documents/list")),
  "DocumentsIndex",
);
const ProductDocumentsPage = lazyRouteComponent(
  withCopy("app.documents", () => import("./documents/list")),
  "ProductDocumentsPage",
);
const DocumentPage = lazyRouteComponent(
  withCopy("app.documents", () => import("./documents/editor")),
  "DocumentPage",
);
const SignPage = lazyRouteComponent(
  withCopy("app.documents", () => import("./documents/sign")),
  "SignPage",
);
const AdvisoriesPage = lazyRouteComponent(
  withCopy("app.advisories", () => import("./advisories/list")),
  "AdvisoriesPage",
);
const AdvisoryPage = lazyRouteComponent(
  withCopy("app.advisories", () => import("./advisories/editor")),
  "AdvisoryPage",
);
const AdvisorySitePage = lazyRouteComponent(
  withCopy("app.advisories", () => import("./advisories/site")),
  "SitePage",
);
const SubscribersPage = lazyRouteComponent(
  withCopy("app.advisories", () => import("./advisories/subscribers")),
  "SubscribersPage",
);
const ReportsPage = lazyRouteComponent(
  withCopy("app.advisories", () => import("./advisories/reports")),
  "ReportsPage",
);
const ReportPage = lazyRouteComponent(
  withCopy("app.advisories", () => import("./advisories/reports")),
  "ReportPage",
);
const DrillsPage = lazyRouteComponent(
  withCopy("app.drills", () => import("./drills/drills")),
  "DrillsPage",
);
const DrillPage = lazyRouteComponent(
  withCopy("app.drills", () => import("./drills/drill")),
  "DrillPage",
);
const DrillReportPage = lazyRouteComponent(
  withCopy("app.drills", () => import("./drills/drill")),
  "DrillReportPage",
);

/** `?product=<uuid>&version=<uuid>`: anything else is "not found". */
function productVersionSearch(search: Record<string, unknown>): { product: string; version: string } {
  if (!isUuid(search.product) || !isUuid(search.version)) throw notFound();
  return { product: search.product, version: search.version };
}
import { Shell } from "./shell/shell";
import { helpSearch } from "./assistant/search";
import { config } from "./config";

/** Where an unsafe path is sent; no route matches it. */
export const UNSAFE_PATH = "/__unsafe-path";

function RouteError({ error, reset }: { error: unknown; reset: () => void }) {
  return (
    <ServerErrorState
      requestId={isApiError(error) ? error.requestId : undefined}
      onRetry={reset}
      statusPageUrl={config.statusPageUrl}
    />
  );
}

/**
 * A 404 of the root or of a layout route replaces the layout (and its link handling): its link still navigates
 * in-app, as a full reload would drop the in-memory session (F11 review L11).
 */
function InAppNotFound() {
  const router = useRouter();
  return (
    <div onClick={interceptLinks((path) => void router.navigate({ to: path }))}>
      <NotFoundState homeHref="/" />
    </div>
  );
}

const rootRoute = createRootRoute({
  // Second line of defence: the raw href as navigated to (publicHref), before the URL parser resolves
  // "%2E%2E" segments (in-app navigation strings, e.g. assistant citations).
  beforeLoad: ({ location }) => {
    const raw = (location as { publicHref?: string }).publicHref ?? location.href;
    if (!isSafeAppPath(raw)) throw notFound();
  },
  component: Outlet,
  notFoundComponent: InAppNotFound,
  errorComponent: RouteError,
});

/** The signed-in layout: every page of the app sits in the shell. */
const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "shell",
  component: Shell,
  notFoundComponent: InAppNotFound,
  errorComponent: RouteError,
});

const homeRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/",
  // No permission here: members without product.read (the Billing role) are sent on by the page itself.
  staticData: { title: (t) => t("shell.dashboard") },
  component: HomePage,
});
const productsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/products",
  staticData: { title: (t) => t("shell.products"), permission: "product.read" },
  component: ProductsPage,
});
const uuidParams =
  (...names: string[]) =>
  ({ params }: { params: Record<string, string> }) => {
    if (names.some((n) => !isUuid(params[n]))) throw notFound();
  };
const productRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/products/$productId",
  staticData: { title: (t) => t("shell.products"), permission: "product.read" },
  beforeLoad: uuidParams("productId"),
  component: ProductPage,
});
const productSourcesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/products/$productId/sources",
  staticData: { title: (t) => t("shell.products"), permission: "product.read" },
  beforeLoad: uuidParams("productId"),
  component: ProductSourcesPage,
});
/** `?tab=` one of the version tabs (anything else is the overview). */
/** `?tab=`, and `?onMarket=1`: open "Place on the market" (the findings' and cases' links, D18). */
const versionSearch = (search: Record<string, unknown>): { tab?: string; onMarket?: boolean } => ({
  ...(typeof search.tab === "string" ? { tab: search.tab } : {}),
  ...(search.onMarket === 1 || search.onMarket === "1" || search.onMarket === true ? { onMarket: true } : {}),
});
const versionRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/products/$productId/versions/$versionId",
  validateSearch: versionSearch,
  staticData: { title: (t) => t("shell.products"), permission: "product.read" },
  beforeLoad: uuidParams("productId", "versionId"),
  component: VersionPage,
});
const componentRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/products/$productId/versions/$versionId/components/$componentId",
  staticData: { title: (t) => t("shell.products"), permission: "product.read" },
  beforeLoad: uuidParams("productId", "versionId", "componentId"),
  component: VersionPage,
});
const diffRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/products/$productId/diff",
  validateSearch: (search: Record<string, unknown>): { version: string; base: string } => {
    if (!isUuid(search.version) || !isUuid(search.base)) throw notFound();
    return { version: search.version, base: search.base };
  },
  staticData: { title: (t) => t("shell.products"), permission: "product.read" },
  beforeLoad: uuidParams("productId"),
  component: DiffPage,
});
/**
 * `?status=open|new&product=<uuid>&exploited=true&caseCoverage=<state>,<state>`: unknown values are dropped (the
 * unfiltered list).
 */
const findingsSearch = (
  search: Record<string, unknown>,
): { status?: "all" | "open" | "new"; product?: string; exploited?: boolean; caseCoverage?: string[] } => {
  const coverage = (typeof search.caseCoverage === "string" ? search.caseCoverage.split(",") : []).filter((c) =>
    (CASE_COVERAGE_STATES as readonly string[]).includes(c),
  );
  return {
    ...(search.status === "open" || search.status === "new" ? { status: search.status } : {}),
    ...(isUuid(search.product) ? { product: search.product } : {}),
    ...(search.exploited === true || search.exploited === "true" ? { exploited: true } : {}),
    ...(coverage.length > 0 ? { caseCoverage: [...new Set(coverage)] } : {}),
  };
};
const findingsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/findings",
  validateSearch: findingsSearch,
  staticData: { title: (t) => t("shell.findings"), permission: "product.read" },
  component: FindingsPage,
});
const findingRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/findings/$findingId",
  staticData: { title: (t) => t("shell.findings"), permission: "product.read" },
  beforeLoad: uuidParams("findingId"),
  component: FindingPage,
});
/** `?status=open|closed` and `?search=<case id>` (the top bar's search, F2). */
const casesSearch = (
  search: Record<string, unknown>,
): { status?: "open" | "closed"; search?: string; new?: "1"; title?: string } => ({
  ...(search.status === "open" || search.status === "closed" ? { status: search.status } : {}),
  ...(typeof search.search === "string" && search.search.length <= 64 ? { search: search.search } : {}),
  // A vulnerability report's "Open a case" (F7b): the dialog opens with the report's title (only shown, never sent).
  ...(search.new === "1" || search.new === 1 ? { new: "1" as const } : {}),
  ...(typeof search.title === "string" ? { title: search.title.slice(0, 255) } : {}),
});
const casesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/cases",
  validateSearch: casesSearch,
  staticData: { title: (t) => t("shell.cases"), permission: "case.read" },
  component: CasesPage,
});
const caseParams = ({ params }: { params: Record<string, string> }) => {
  if (!isUuid(params.caseId)) throw notFound();
  if (params.stage !== undefined && !isStageId(params.stage)) throw notFound();
};
/** `?acknowledge=1` (an alert e-mail link: only the banner, never an action on GET, REQUESTS #8); `?method=`. */
const caseSearch = (
  search: Record<string, unknown>,
): { acknowledge?: string; method?: string; reference?: string } => ({
  ...(typeof search.acknowledge === "string" || typeof search.acknowledge === "number"
    ? { acknowledge: String(search.acknowledge) }
    : {}),
  ...(search.method === "email_fallback" || search.method === "extension" ? { method: search.method } : {}),
  // The SRP notification id the extension read from the SRP's confirmation (F10); prefilled, still editable.
  ...(search.method === "extension" &&
  typeof search.reference === "string" &&
  /^[A-Z0-9][A-Z0-9-]{3,63}$/.test(search.reference)
    ? { reference: search.reference }
    : {}),
});
const caseRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/cases/$caseId",
  validateSearch: caseSearch,
  staticData: { title: (t) => t("shell.cases"), permission: "case.read" },
  beforeLoad: caseParams,
  component: CaseRedirect,
});
const stageRoute = (path: string) =>
  createRoute({
    getParentRoute: () => shellRoute,
    path,
    validateSearch: caseSearch,
    staticData: { title: (t) => t("shell.cases"), permission: "case.read" },
    beforeLoad: caseParams,
    component: CaseStagePage,
  });
const caseStageRoute = stageRoute("/cases/$caseId/stages/$stage");
const caseCopyRoute = stageRoute("/cases/$caseId/stages/$stage/copy");
const caseSubmitRoute = stageRoute("/cases/$caseId/stages/$stage/submit");
const caseNoticeRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/cases/$caseId/notice",
  staticData: { title: (t) => t("shell.cases"), permission: "case.read" },
  beforeLoad: caseParams,
  component: NoticePage,
});
const documentsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/documents",
  staticData: { title: (t) => t("shell.documents"), permission: "product.read" },
  component: DocumentsIndex,
});
const productDocumentsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/products/$productId/documents",
  staticData: { title: (t) => t("shell.documents"), permission: "product.read" },
  beforeLoad: uuidParams("productId"),
  component: ProductDocumentsPage,
});
const documentRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/products/$productId/documents/$documentId",
  staticData: { title: (t) => t("shell.documents"), permission: "product.read" },
  beforeLoad: uuidParams("productId", "documentId"),
  component: DocumentPage,
});
const documentSignRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/products/$productId/documents/$documentId/sign",
  staticData: { title: (t) => t("shell.documents"), permission: "document.sign" },
  beforeLoad: uuidParams("productId", "documentId"),
  component: SignPage,
});
const advisoriesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/advisories",
  staticData: { title: (t) => t("shell.advisories"), permission: "case.read" },
  component: AdvisoriesPage,
});
const advisorySiteRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/advisories/site",
  staticData: { title: (t) => t("shell.advisories"), permission: "advisory.publish" },
  component: AdvisorySitePage,
});
const advisorySubscribersRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/advisories/subscribers",
  staticData: { title: (t) => t("shell.advisories"), permission: "advisory.publish" },
  component: SubscribersPage,
});
const advisoryRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/advisories/$advisoryId",
  staticData: { title: (t) => t("shell.advisories"), permission: "case.read" },
  beforeLoad: uuidParams("advisoryId"),
  component: AdvisoryPage,
});
/** `?status=` one of the report statuses (`throttled` from the daily summary e-mail); anything else: all. */
const reportsSearch = (search: Record<string, unknown>): { status?: string } =>
  typeof search.status === "string" && ["new", "throttled", "triaged", "closed", "spam"].includes(search.status)
    ? { status: search.status }
    : {};
const reportsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/vulnerability-reports",
  validateSearch: reportsSearch,
  staticData: { title: (t) => t("shell.advisories"), permission: "case.read" },
  component: ReportsPage,
});
const reportRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/vulnerability-reports/$reportId",
  staticData: { title: (t) => t("shell.advisories"), permission: "case.read" },
  beforeLoad: uuidParams("reportId"),
  component: ReportPage,
});
const integrationsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/integrations",
  staticData: {
    title: (t) => t("shell.api_integrations"),
    permission: { anyOf: ["integration.manage", "source.push"] },
  },
  component: IntegrationsPage,
});
const webhookRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/integrations/webhooks/$webhookId",
  staticData: { title: (t) => t("workspace.webhook"), permission: "integration.manage" },
  beforeLoad: uuidParams("webhookId"),
  component: WebhookPage,
});
const teamRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/team",
  staticData: { title: (t) => t("shell.team"), permission: "member.manage" },
  component: TeamPage,
});
// The role matrix is visible to every member (HANDOFF screen 31); editing roles needs member.manage.
const teamRolesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/team/roles",
  staticData: { title: (t) => t("workspace.roles_and_permissions") },
  component: RolesPage,
});
/** Settings (screens 32/33): a layout with section links; each section is a child route. */
const settingsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/settings",
  staticData: { title: (t) => t("shell.settings") },
  component: SettingsLayout,
});
const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/",
  component: SettingsIndex,
});
const settingsMeRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "me",
  staticData: { title: (t) => t("shell.settings") },
  component: MeSettingsPage,
});
const settingsCompanyRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "company",
  staticData: { title: (t) => t("onboarding.your_company"), permission: "settings.manage" },
  component: CompanySettingsPage,
});
const settingsWorkspaceRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "workspace",
  staticData: { title: (t) => t("shell.ws_preferences") },
  component: PreferencesPage,
});
const settingsSecurityRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "security",
  staticData: { title: (t) => t("workspace.security"), permission: "settings.manage" },
  component: SecuritySettingsPage,
});
const settingsNotificationsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "notifications",
  staticData: { title: (t) => t("workspace.notifications") },
  component: NotificationSettings,
});
/** `?export=<uuid>` from the export e-mail (the token is in the fragment, never in the query). */
const settingsDataRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "data",
  validateSearch: (search: Record<string, unknown>): { export?: string } =>
    isUuid(search.export) ? { export: search.export } : {},
  staticData: {
    title: (t) => t("workspace.data"),
    permission: { anyOf: ["settings.manage", "workspace.delete"] },
  },
  component: DataSettingsPage,
});
const settingsAuditRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "audit",
  staticData: { title: (t) => t("shell.audit_log"), permission: "audit.read" },
  component: AuditPage,
});
const billingRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "billing",
  staticData: { title: (t) => t("workspace.billing"), permission: "billing.manage" },
  component: BillingPage,
});
const drillsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/drills",
  staticData: { title: (t) => t("shell.fire_drills"), permission: "case.read" },
  component: DrillsPage,
});
const drillRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/drills/$drillId",
  staticData: { title: (t) => t("shell.fire_drills"), permission: "case.read" },
  beforeLoad: uuidParams("drillId"),
  component: DrillPage,
});
const drillReportRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/drills/$drillId/report",
  staticData: { title: (t) => t("shell.fire_drills"), permission: "case.read" },
  beforeLoad: uuidParams("drillId"),
  component: DrillReportPage,
});
const helpRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/help",
  staticData: { title: (t) => t("shell.help") },
  validateSearch: helpSearch,
  component: HelpPage,
});
const supportThreadRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/help/support/$threadId",
  staticData: { title: (t) => t("shell.help") },
  beforeLoad: uuidParams("threadId"),
  component: SupportThreadRoute,
});
const notificationsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/notifications",
  staticData: { title: (t) => t("ui.notifications") },
  component: NotificationsPage,
});
// Onboarding (04 §1.3–1.5): its own frame, no sidebar; the same /me, workspace and permission gate.
const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "wizard",
  component: OnboardingFrame,
  notFoundComponent: InAppNotFound,
  errorComponent: RouteError,
});
const onboardingStartRoute = createRoute({
  getParentRoute: () => onboardingRoute,
  path: "/onboarding",
  staticData: { title: (t) => t("onboarding.onboarding"), step: 0 },
  component: OnboardingStart,
});
const companyRoute = createRoute({
  getParentRoute: () => onboardingRoute,
  path: "/onboarding/company",
  staticData: { title: (t) => t("onboarding.your_company"), permission: "settings.manage", step: 0 },
  component: CompanyPage,
});
const productStepRoute = createRoute({
  getParentRoute: () => onboardingRoute,
  path: "/onboarding/product",
  staticData: { title: (t) => t("onboarding.your_first_product"), permission: "product.manage", step: 1 },
  component: FirstProductPage,
});
const sourcesRoute = createRoute({
  getParentRoute: () => onboardingRoute,
  path: "/onboarding/sources",
  validateSearch: productVersionSearch,
  staticData: { title: (t) => t("onboarding.sources"), permission: "source.manage", step: 2 },
  component: SourcesPage,
});
const scanRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/products/$productId/versions/$versionId/scans/$scanId",
  staticData: { title: (t) => t("shell.scan"), permission: "product.read" },
  beforeLoad: ({ params }) => {
    if (!isUuid(params.productId) || !isUuid(params.versionId) || !isUuid(params.scanId)) throw notFound();
  },
  component: ScanPage,
});
const resultsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/onboarding/results/$versionId",
  validateSearch: (search: Record<string, unknown>): { product: string } => {
    if (!isUuid(search.product)) throw notFound();
    return { product: search.product };
  },
  staticData: { title: (t) => t("onboarding.scan_finished"), permission: "product.read" },
  beforeLoad: ({ params }) => {
    if (!isUuid(params.versionId)) throw notFound();
  },
  component: ResultsPage,
});
const githubCallbackRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/integrations/github/callback",
  staticData: { title: (t) => t("onboarding.github"), permission: "integration.manage" },
  component: GitHubCallback,
});
const gitlabCallbackRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/integrations/gitlab/callback",
  staticData: { title: (t) => t("onboarding.gitlab"), permission: "integration.manage" },
  component: GitLabCallback,
});
/** The invitation link (REQUESTS #8: never acts on GET); outside the shell's workspace gate. */
const acceptInvitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invitations/accept",
  validateSearch: (search: Record<string, unknown>): { token?: string } =>
    typeof search.token === "string" && search.token.length <= 512 ? { token: search.token } : {},
  component: AcceptInvitationPage,
});
const moreRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/more",
  staticData: { title: (t) => t("shell.more") },
  component: MorePage,
});

export const routeTree = rootRoute.addChildren([
  acceptInvitationRoute,
  shellRoute.addChildren([
    homeRoute,
    productsRoute,
    productRoute,
    productSourcesRoute,
    versionRoute,
    componentRoute,
    diffRoute,
    findingsRoute,
    findingRoute,
    casesRoute,
    caseRoute,
    caseStageRoute,
    caseCopyRoute,
    caseSubmitRoute,
    caseNoticeRoute,
    documentsRoute,
    productDocumentsRoute,
    documentRoute,
    documentSignRoute,
    advisoriesRoute,
    advisorySiteRoute,
    advisorySubscribersRoute,
    advisoryRoute,
    reportsRoute,
    reportRoute,
    integrationsRoute,
    webhookRoute,
    teamRoute,
    teamRolesRoute,
    settingsRoute.addChildren([
      settingsIndexRoute,
      settingsMeRoute,
      settingsCompanyRoute,
      settingsWorkspaceRoute,
      settingsSecurityRoute,
      settingsNotificationsRoute,
      settingsDataRoute,
      settingsAuditRoute,
      billingRoute,
    ]),
    drillsRoute,
    drillRoute,
    drillReportRoute,
    helpRoute,
    supportThreadRoute,
    notificationsRoute,
    moreRoute,
    scanRoute,
    resultsRoute,
    githubCallbackRoute,
    gitlabCallbackRoute,
  ]),
  onboardingRoute.addChildren([onboardingStartRoute, companyRoute, productStepRoute, sourcesRoute]),
]);

export function createAppRouter(history?: RouterHistory) {
  return createRouter({
    routeTree,
    history,
    defaultPreload: "intent",
    rewrite: {
      input: ({ url }) => (isSafeAppPath(url.pathname) ? url : new URL(UNSAFE_PATH, url.origin)),
    },
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
  interface StaticDataRouteOption {
    /** The page's name ("You can't open {page}"). */
    title?: (t: TFunction) => string;
    /** What the member needs to open the page (11 §3); checked against `GET /me` by the shell. */
    permission?: PermissionRule;
    /** Onboarding: the step shown in the progress indicator (0–3). */
    step?: number;
  }
}
